import { useEffect, useRef, useState } from "react";

/**
 * Holds the screen awake (via the Screen Wake Lock API) while `active` is true —
 * e.g. so the display doesn't sleep while someone cooks from a recipe.
 *
 * The OS releases a wake lock whenever the tab is hidden or the screen turns
 * off, so we re-request it on `visibilitychange` to keep it alive across
 * backgrounding. Requesting can reject (low battery, denied) — that's non-fatal
 * and swallowed. Returns whether the API exists so callers can hide the control
 * on unsupported browsers.
 */
export function useWakeLock(active: boolean): { supported: boolean } {
    const [supported] = useState(() => typeof navigator !== "undefined" && "wakeLock" in navigator);
    const sentinelRef = useRef<WakeLockSentinel | null>(null);

    useEffect(() => {
        if (!supported || !active) return;

        let cancelled = false;

        const acquire = async () => {
            try {
                const sentinel = await navigator.wakeLock.request("screen");
                if (cancelled) {
                    // Teardown happened while the request was in flight.
                    sentinel.release().catch(() => {});
                    return;
                }
                sentinelRef.current = sentinel;
            } catch {
                // Denied or unavailable (e.g. low battery) — nothing to do.
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible" && !cancelled) {
                acquire();
            }
        };

        acquire();
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            cancelled = true;
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            sentinelRef.current?.release().catch(() => {});
            sentinelRef.current = null;
        };
    }, [active, supported]);

    return { supported };
}
